
import { supabase } from "@/integrations/supabase/client";

export interface Resource {
  id: string;
  title: string;
  description: string;
  url: string;
  type: 'guide' | 'tool' | 'template' | 'video' | 'article';
  category: string;
  difficulty_level?: 'beginner' | 'intermediate' | 'advanced';
  provider?: string;
  is_featured: boolean;
  is_approved: boolean;
  rating_avg: number;
  rating_count: number;
  view_count: number;
  bookmark_count: number;
  thumbnail_url?: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  created_by?: string;
  pricing_info?: any;
}

export interface ResourceCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  display_order: number;
  created_at: string;
}

export interface UserBookmark {
  id: string;
  user_id: string;
  resource_id: string;
  created_at: string;
}

export interface ResourceRating {
  id: string;
  user_id: string;
  resource_id: string;
  rating: number;
  review?: string;
  created_at: string;
  updated_at: string;
}

export const fetchResources = async (filters?: {
  category?: string;
  type?: string;
  featured?: boolean;
  difficulty?: string;
  tags?: string[];
}) => {
  let query = supabase
    .from('resources')
    .select('*')
    .eq('is_approved', true)
    .order('created_at', { ascending: false });

  if (filters?.category) {
    query = query.eq('category', filters.category);
  }
  if (filters?.type) {
    query = query.eq('type', filters.type);
  }
  if (filters?.featured) {
    query = query.eq('is_featured', true);
  }
  if (filters?.difficulty) {
    query = query.eq('difficulty_level', filters.difficulty);
  }
  if (filters?.tags && filters.tags.length > 0) {
    query = query.overlaps('tags', filters.tags);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as Resource[];
};

export const fetchAllResources = async () => {
  const { data, error } = await supabase
    .from('resources')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Resource[];
};

export const createResource = async (resourceData: Omit<Resource, 'id' | 'created_at' | 'updated_at' | 'rating_avg' | 'rating_count' | 'view_count' | 'bookmark_count'>) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Must be logged in to create resource');

  const { data, error } = await supabase
    .from('resources')
    .insert({
      ...resourceData,
      created_by: user.id,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Resource;
};

export const updateResource = async (id: string, resourceData: Partial<Resource>) => {
  const { data, error } = await supabase
    .from('resources')
    .update(resourceData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Resource;
};

export const deleteResource = async (id: string) => {
  const { error } = await supabase
    .from('resources')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export const approveResource = async (id: string) => {
  return updateResource(id, { is_approved: true });
};

export const fetchResourceCategories = async () => {
  const { data, error } = await supabase
    .from('resource_categories')
    .select('*')
    .order('display_order');

  if (error) throw error;
  return data as ResourceCategory[];
};

export const toggleBookmark = async (resourceId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Must be logged in to bookmark');

  // Check if bookmark exists
  const { data: existing } = await supabase
    .from('user_bookmarks')
    .select('id')
    .eq('user_id', user.id)
    .eq('resource_id', resourceId)
    .single();

  if (existing) {
    // Remove bookmark
    const { error } = await supabase
      .from('user_bookmarks')
      .delete()
      .eq('id', existing.id);
    if (error) throw error;
    return false;
  } else {
    // Add bookmark
    const { error } = await supabase
      .from('user_bookmarks')
      .insert({ user_id: user.id, resource_id: resourceId });
    if (error) throw error;
    return true;
  }
};

export const rateResource = async (resourceId: string, rating: number, review?: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Must be logged in to rate');

  const { error } = await supabase
    .from('resource_ratings')
    .upsert({
      user_id: user.id,
      resource_id: resourceId,
      rating,
      review
    });

  if (error) throw error;
};

export const fetchUserBookmarks = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('user_bookmarks')
    .select('resource_id')
    .eq('user_id', user.id);

  if (error) throw error;
  return data?.map(b => b.resource_id) || [];
};
